import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Calendar as CalendarIcon } from "lucide-react";
import { callSheetApi } from "@/lib/api";

// Adds a new shoot day to the current project.
// Reuses the existing POST /projects/:id/call-sheets endpoint which:
//   1. Creates a new CallSheet (day) document
//   2. Auto-creates a Production-phase task in the Plan engine (cascade)
//   3. Emits a 'day.created' webhook event
export default function AddShootDateDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  onAdded,
}) {
  const [shootDate, setShootDate] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setShootDate("");
    setLabel("");
  };

  const handleSave = async () => {
    if (!shootDate) {
      toast.error("Pick a shoot date");
      return;
    }
    setSaving(true);
    try {
      const day = await callSheetApi.addDay(projectId, {
        shoot_date: shootDate,
        day_label: label.trim(),
      });
      toast.success(
        `Shoot day added · ${day.shoot_date}${day.day_label ? ` · ${day.day_label}` : ""}`,
      );
      reset();
      onOpenChange(false);
      onAdded?.(day);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add shoot day");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-[440px]"
        data-testid="add-shoot-date-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-500" />
            Add shoot date
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Creates a new shoot day on <span className="text-zinc-300">{projectName}</span>{" "}
            with an empty call sheet. A matching Production-phase block is
            added to the project plan automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500" htmlFor="add-shoot-date">
              Shoot date
            </Label>
            <Input
              id="add-shoot-date"
              type="date"
              data-testid="add-shoot-date-input"
              value={shootDate}
              onChange={(e) => setShootDate(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500" htmlFor="add-shoot-label">
              Label (optional)
            </Label>
            <Input
              id="add-shoot-label"
              data-testid="add-shoot-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Studio · Day 2 — Beach"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
            <p className="text-[10px] text-zinc-600 mt-1">
              Defaults to &ldquo;Day {`{N}`}&rdquo; if left blank.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400"
            data-testid="add-shoot-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !shootDate}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="add-shoot-save-btn"
          >
            {saving && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            Add shoot day
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
